import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Form, 
  Input, 
  List, 
  Avatar, 
  Typography, 
  Space, 
  Divider,
  Empty,
  message,
  Spin
} from 'antd';
import { 
  MessageOutlined, 
  SendOutlined, 
  UserOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CommentSectionProps, SubtaskComment } from '../../../types/plan-sub-task.ts';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

/**
 * Comment Section Component
 * Handles displaying and adding comments for subtasks
 */
const CommentSection: React.FC<CommentSectionProps> = ({
  subtaskCode,
  comments,
  onAddComment,
  loading,
  showCard = true
}) => {
  const { t } = useTranslation();
  const [commentForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [localComments, setLocalComments] = useState<SubtaskComment[]>(comments);

  // Update local comments when props change
  useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  // Handle comment submission
  const handleSubmit = async () => {
    try {
      const values = await commentForm.validateFields();
      setSubmitting(true);

      await onAddComment(values.content);
      
      // Clear form
      commentForm.resetFields();
      message.success(t('subtask.comments.addCommentSuccess'));
    } catch (error: any) {
      if (error?.errorFields) {
        // Form validation error
        message.error(t('subtask.comments.enterContent'));
      } else {
        console.error('Error adding comment:', error);
        message.error(t('subtask.comments.addCommentFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Format comment time
  const formatCommentTime = (timeString: string) => {
    const time = new Date(timeString);
    const now = new Date();
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) {
      return t('subtask.comments.justNow');
    } else if (diffMins < 60) {
      return t('subtask.comments.minutesAgo', { minutes: diffMins });
    } else if (diffHours < 24) {
      return t('subtask.comments.hoursAgo', { hours: diffHours });
    } else if (diffDays < 7) {
      return t('subtask.comments.daysAgo', { days: diffDays });
    } else {
      return time.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Render comment form
  const renderCommentForm = () => (
    <div style={{ marginBottom: 24 }}>
      <Form form={commentForm} onFinish={handleSubmit}>
        <Form.Item
          name="content"
          rules={[
            { required: true, message: t('subtask.comments.contentRequired') },
            { min: 1, message: t('subtask.comments.contentEmpty') },
            { max: 1000, message: t('subtask.comments.contentTooLong') }
          ]}
        >
          <TextArea
            rows={4}
            placeholder={t('subtask.comments.placeholder')}
            showCount
            maxLength={1000}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SendOutlined />}
            loading={submitting}
          >
            {t('subtask.comments.submit')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );

  // Render comment list
  const renderCommentList = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">{t('subtask.comments.loading')}</Text>
          </div>
        </div>
      );
    }

    if (localComments.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('subtask.comments.noComments')}
          style={{ padding: '40px 0' }}
        >
          <Text type="secondary">{t('subtask.comments.beFirstToComment')}</Text>
        </Empty>
      );
    }

    return (
      <List
        itemLayout="vertical"
        dataSource={localComments}
        renderItem={(comment) => (
          <List.Item
            key={comment.id}
            style={{
              padding: '16px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            <List.Item.Meta
              avatar={
                <Avatar 
                  icon={<UserOutlined />} 
                  style={{ backgroundColor: '#1890ff' }}
                />
              }
              title={
                <Space>
                  <Text strong>{comment.author}</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {formatCommentTime(comment.gmtCreate)}
                  </Text>
                </Space>
              }
              description={
                <Paragraph 
                  style={{ 
                    marginBottom: 0, 
                    marginTop: 8,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {comment.content}
                </Paragraph>
              }
            />
          </List.Item>
        )}
      />
    );
  };

  const title = (
    <Space>
      <MessageOutlined />
      <span>{t('subtask.detail.tabs.comments')}</span>
      {localComments.length > 0 && (
        <Text type="secondary">({localComments.length})</Text>
      )}
    </Space>
  );

  const content = (
    <>
      {renderCommentForm()}
      <Divider />
      {renderCommentList()}
    </>
  );

  if (showCard) {
    return (
      <Card 
        title={title}
        className="detail-section"
      >
        {content}
      </Card>
    );
  }

  return (
    <div>
      {content}
    </div>
  );
};

export default CommentSection;